import { lazy, Suspense, useEffect, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { LoadingSpinner } from './LoadingSpinner';

// Lazy-load overlay panels (same components, loaded on demand)
const OVERLAY_MAP = {
    deck: lazy(() => import('../pages/DeckBuilder')),
    equipment: lazy(() => import('../pages/Equipment')),
    settings: lazy(() => import('../pages/Settings')),
};

/**
 * OverlayShell — renders the active overlay panel on top of the Phaser canvas.
 *
 * - Backdrop click closes the overlay
 * - ESC key closes the overlay
 * - Passes `onClose` prop to the panel component
 */
export default function OverlayShell() {
    const activeOverlay = useStore(s => s.activeOverlay);
    const closeOverlay = useStore(s => s.closeOverlay);

    // Close on ESC key
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') closeOverlay();
    }, [closeOverlay]);

    useEffect(() => {
        if (!activeOverlay) return;
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeOverlay, handleKeyDown]);

    if (!activeOverlay) return null;

    const Panel = OVERLAY_MAP[activeOverlay];
    if (!Panel) {
        console.warn(`[OverlayShell] unknown overlay: ${activeOverlay}`);
        return null;
    }

    return (
        <div className="overlay-shell" onClick={closeOverlay}>
            <div className="overlay-panel" onClick={e => e.stopPropagation()}>
                <button
                    className="overlay-close-btn"
                    onClick={closeOverlay}
                    aria-label="Close"
                >
                    ✕
                </button>
                <Suspense fallback={<LoadingSpinner text="Loading..." />}>
                    <Panel onClose={closeOverlay} />
                </Suspense>
            </div>
        </div>
    );
}
